<template>
  <div class="row">
    <div class="col-8 mb-2">
      <InputFieldText
        :label-name="t('FULL_NAME')"
        v-model="formData.fullName"
        :required="true"
        class="customer_full_name"
        data-testid="customer_full_name"
        @update:modelValue="validateAndEmitUpdate"
      />
    </div>
    <div class="col-8 mb-2">
      <p>
        {{ t("USER_INFO_ID_FULL_NAME") }}
      </p>
    </div>
    <div class="col-8 mb-2">
      <h2 class="fs-1">{{ t("USER_ADDRESS") }}</h2>
      <InputFieldZipCode
        data-testid="customer-address-postal-code"
        :className="'col me-1'"
        :labelName="t('USER_POSTAL_CODE_FIELD')"
        :inputId="'postalCode'"
        :required="true"
        :maxLength="5"
        v-model="formData.postalCode"
        @update:model-value="validateAndEmitUpdate"
      />
    </div>

    <div class="col-8 mb-5">
      <Dropdown
        :dropdown-Label="t('REGENCY_CITY')"
        :options="dropdownOptions.kabupatenKota"
        v-model="formData.selectedKabupatenKota"
        @update:modelValue="validateAndEmitUpdate"
        data-testid="regency_city"
        placeholder="--"
        :required="true"
      />
    </div>

    <div class="col-8 mb-2">
      <h2 class="fs-1">{{ t("ADDITIONAL_INFO") }}</h2>
      <Dropdown
        v-if="!isRentalFlow"
        :dropdown-Label="t('LATEST_EDUCATION_DEGREE')"
        :options="dropdownOptions.educationDegree"
        v-model="formData.selectedEducationDegree"
        @update:modelValue="validateAndEmitUpdate"
        data-testid="latest_education_degree"
        placeholder="--"
        :required="true"
      />
    </div>

    <div v-if="!isRentalFlow" class="col-8 mb-2">
      <Dropdown
        :dropdown-Label="t('TARGET_MARKET')"
        :options="dropdownOptions.targetMarket"
        v-model="formData.selectedTargetMarket"
        @update:modelValue="validateAndEmitUpdate"
        data-testid="target_market"
        placeholder="--"
        :required="true"
      />
    </div>

    <div class="col-8 mb-2">
      <Dropdown
        :dropdown-Label="t('OCCUPATION')"
        :options="dropdownOptions.occupations"
        v-model="formData.selectedOccupation"
        @update:modelValue="validateAndEmitUpdate"
        data-testid="occupation"
        placeholder="--"
        :required="true"
      />
    </div>

    <div v-if="!isRentalFlow" class="col-8 mb-2">
      <InputFieldText
        :label-name="t('EMPLOYER_NAME')"
        v-model="formData.employerName"
        :required="true"
        class="customer_employer_name"
        data-testid="customer_employer_name"
        @update:modelValue="validateAndEmitUpdate"
      />
    </div>

    <div class="col-8 mb-2">
      <InputFieldNumber
        :label-name="t('MONTHLY_INCOME')"
        v-model="formData.monthlyIncome"
        :required="true"
        data-testid="customer_monthly_income"
        @update:modelValue="validateAndEmitUpdate"
      />
    </div>

    <div v-if="!isRentalFlow" class="col-8 mb-2">
      <InputFieldText
        :label-name="t('MOTHER_MAIDEN_NAME')"
        v-model="formData.motherMaidenName"
        :required="true"
        class="customer_mother_maiden_name"
        data-testid="customer_mother_maiden_name"
        @update:modelValue="validateAndEmitUpdate"
      />
    </div>

    <div class="col-8 mb-2">
      <InputFieldText
        :label-name="isRentalFlow ? t('EMERGENCY_CONTACT') : t('REFERRAL_NAME')"
        v-model="formData.referralName"
        :required="true"
        class="customer_referral_name"
        data-testid="customer_referral_name"
        @update:modelValue="validateAndEmitUpdate"
      />
    </div>

    <div v-if="!isRentalFlow" class="col-8 mb-5">
      <InputFieldText
        :label-name="t('REFERRAL_ADDRESS')"
        v-model="formData.referralAddress"
        :required="true"
        class="customer_referral_address"
        data-testid="customer_referral_address"
        @update:modelValue="validateAndEmitUpdate"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watchEffect, watch, WritableComputedRef } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import Dropdown from "@/apps/merchant3/shared-components/ui/Dropdown.vue";
import InputFieldNumber from "@/apps/admin-console/components/ui/input-fields/AdminInputFieldNumber.vue";
import InputFieldText from "@/apps/merchant3/shared-components/input-fields/InputFieldText.vue";
import InputFieldZipCode from "@/apps/merchant3/shared-components/input-fields/InputFieldZipCode.vue";
import {
  REGENCY_CITY,
  OCCUPATIONS,
  EDUCATION_DEGREES,
  TARGET_MARKETS,
} from "./const/IndonesiaConsts";
import { IndonesiaInfo, FormData } from "./types/IndonesiaTypes";
import { useStore } from "vuex";
import { capitalizeWords } from "@/helpers/strings-helpers";
import { isRentalFlow } from "@/apps/merchant3/helpers/checkoutFlows";

const props = defineProps<{
  initialData?: Record<string, string>;
}>();

const { t } = useI18n();

const emit = defineEmits<{
  (e: "update:data", data: { isValid: boolean; data: IndonesiaInfo }): void;
}>();

const store = useStore();

const customerName: WritableComputedRef<string> = computed({
  get() {
    return store.state.customer?.name
      ? capitalizeWords(store.state.customer.name)
      : "";
  },
  set(newValue) {
    formData.fullName = newValue;
  },
});

const formData = reactive<FormData>({
  fullName: customerName.value,
  postalCode: "",
  selectedKabupatenKota: null,
  selectedEducationDegree: null,
  selectedTargetMarket: null,
  monthlyIncome: "",
  selectedOccupation: null,
  motherMaidenName: "",
  referralName: "",
  referralAddress: "",
  employerName: "",
});

const dropdownOptions = {
  educationDegree: EDUCATION_DEGREES,
  targetMarket: TARGET_MARKETS,
  kabupatenKota: REGENCY_CITY,
  occupations: OCCUPATIONS,
} as const;

const applyInitialData = (data: Record<string, string>) => {
  if (data["full-name"]) formData.fullName = data["full-name"];
  if (data["zip-code"]) formData.postalCode = data["zip-code"];
  if (data["monthly-income"]) formData.monthlyIncome = data["monthly-income"];
  if (data["referral-name"]) formData.referralName = data["referral-name"];
  if (data["regency-city"]) {
    const val = data["regency-city"];
    formData.selectedKabupatenKota =
      dropdownOptions.kabupatenKota.find(
        (o) => o.name === val || o.code === val
      ) ?? null;
  }
  if (data["occupation"]) {
    const val = data["occupation"];
    formData.selectedOccupation =
      dropdownOptions.occupations.find(
        (o) => o.name === val || o.code === val
      ) ?? null;
  }
  if (data["latest-education-degree"]) {
    const val = data["latest-education-degree"];
    formData.selectedEducationDegree =
      dropdownOptions.educationDegree.find(
        (o) => o.name === val || o.code === val
      ) ?? null;
  }
  if (data["target-market"]) {
    const val = data["target-market"];
    formData.selectedTargetMarket =
      dropdownOptions.targetMarket.find(
        (o) => o.name === val || o.code === val
      ) ?? null;
  }
  if (data["employer-name"]) formData.employerName = data["employer-name"];
  if (data["mothers-maiden-name"])
    formData.motherMaidenName = data["mothers-maiden-name"];
  if (data["referral-address"])
    formData.referralAddress = data["referral-address"];
};

watch(
  () => props.initialData,
  (data) => {
    if (data && Object.keys(data).length > 0) applyInitialData(data);
  },
  { immediate: true }
);

const isValid = computed(() => {
  const baseValid = Boolean(
    formData.postalCode &&
      formData.selectedKabupatenKota &&
      formData.monthlyIncome &&
      formData.selectedOccupation &&
      formData.referralName
  );
  if (isRentalFlow.value) return baseValid;
  return (
    baseValid &&
    Boolean(
      formData.selectedEducationDegree &&
        formData.selectedTargetMarket &&
        formData.motherMaidenName &&
        formData.referralAddress &&
        formData.employerName
    )
  );
});

const validateAndEmitUpdate = () => {
  if (
    !formData.fullName ||
    !formData.selectedKabupatenKota ||
    !formData.selectedOccupation
  ) {
    return;
  }
  if (
    !isRentalFlow.value &&
    (!formData.selectedEducationDegree || !formData.selectedTargetMarket)
  ) {
    return;
  }

  const data: IndonesiaInfo = {
    "full-name": formData.fullName,
    "zip-code": formData.postalCode,
    "regency-city": formData.selectedKabupatenKota.name,
    occupation: formData.selectedOccupation.name,
    "monthly-income": formData.monthlyIncome,
    "referral-name": formData.referralName,
    ...(isRentalFlow.value
      ? {}
      : {
          "latest-education-degree": formData.selectedEducationDegree!.name,
          "target-market": formData.selectedTargetMarket!.name,
          "employer-name": formData.employerName,
          "mothers-maiden-name": formData.motherMaidenName,
          "referral-address": formData.referralAddress,
        }),
  };

  emit("update:data", { isValid: isValid.value, data });
};

watchEffect(() => {
  validateAndEmitUpdate();
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

h2 {
  color: $pj-color-black300;
}
</style>
